import React, { useCallback, useState, memo } from "react";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { isNull } from "lodash";

import { getFormattedNumber } from "../../../utils";

const headCells = [
  "Select",
  "Mbr#",
  "Store Name",
  "Contact Person",
  "Phone",
  "Delivery Street Address",
  "Delivery City",
  "Delivery State",
  "Country",
  "Delivery Zip Code",
  "Distribution Center",
  "Order Type",
  "Store Size",
  "Lineal Footage",
  "Project Type",
  "Project Planner",
  "Assigned MAC",
  "Credit Limit",
  "Project Status",
  "Created On",
];

const ModelStoreDialogContent = (props) => {
  const { rows = [], handleStoreSelection } = props;
  const [currentSelection, setCurrentSelection] = useState(0);

  const handleRowSelection = useCallback(
    (event) => {
      const currentSelection = Number(event.target.value);
      setCurrentSelection(currentSelection);
      handleStoreSelection(currentSelection);
    },
    [handleStoreSelection]
  );

  return (
    <>
      <Typography mb={2}>Select store details from below table:</Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 290 }}>
        <Table
          stickyHeader
          sx={{ minWidth: 850 }}
          aria-label="Model Store details table"
        >
          <TableHead>
            <TableRow>
              {headCells.map((headCell, i) => (
                <TableCell
                  key={i}
                  padding="none"
                  sx={{
                    pl: 1,
                    py: 1,
                    fontWeight: "bold",
                    backgroundColor: "#E7E7E7",
                  }}
                >
                  {headCell}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                hover
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                key={i}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    maxWidth: 50,
                  }}
                >
                  <FormControlLabel
                    onChange={handleRowSelection}
                    checked={currentSelection === i}
                    value={i}
                    control={<Radio />}
                    label=""
                  />
                </TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 75,
                  }}
                >
                  <Tooltip
                    title={
                      isNull(row?.CheckDigit)
                        ? row?.MemberNumber ?? ""
                        : `${row?.MemberNumber}-${row?.CheckDigit}`
                    }
                    arrow
                  >
                    <span>
                      {isNull(row?.CheckDigit)
                        ? row?.MemberNumber
                        : `${row?.MemberNumber}-${row?.CheckDigit}`}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.StoreName ?? ""} arrow>
                    <span>{row?.StoreName}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.ContactPerson ?? ""} arrow>
                    <span>{row?.ContactPerson}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 70,
                  }}
                >
                  <Tooltip title={row?.ContactPhone ?? ""} arrow>
                    <span>{row?.ContactPhone}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.StreetAddress ?? ""} arrow>
                    <span>{row?.StreetAddress}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.City ?? ""} arrow>
                    <span>{row?.City}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.State ?? ""} arrow>
                    <span> {row?.State}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.CountryName ?? ""} arrow>
                    <span> {row?.CountryName}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 50,
                  }}
                >
                  <Tooltip title={row?.ZipCode ?? ""} arrow>
                    <span>{row?.ZipCode}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.DistributionCenter ?? ""} arrow>
                    <span>{row?.DistributionCenter}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.OrderType ?? ""} arrow>
                    <span>{row?.OrderType}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 50,
                  }}
                >
                  {!!row?.StoreSize ? (
                    <Tooltip title={`${row?.StoreSize} sq ft`} arrow>
                      <span>{row?.StoreSize} sq ft</span>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 50,
                  }}
                >
                  {!!row?.LinealFootage ? (
                    <Tooltip title={`${row?.LinealFootage} sq ft`} arrow>
                      <span>{row?.LinealFootage} sq ft</span>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.ProjectType ?? ""} arrow>
                    <span>{row?.ProjectType}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.AssignedPlanner ?? ""} arrow>
                    <span>{row?.AssignedPlanner}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.AssignedMac ?? ""} arrow>
                    <span>{row?.AssignedMac}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  {!!row?.CreditLimit ? (
                    <Tooltip
                      title={`$${getFormattedNumber(row?.CreditLimit)}`}
                      arrow
                    >
                      <span>${getFormattedNumber(row?.CreditLimit)}</span>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.ProjectStatus ?? ""} arrow>
                    <span>{row?.ProjectStatus}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    pl: 1,
                    display: "table-cell",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row?.CreatedDate ?? ""} arrow>
                    <span>{row?.CreatedDate}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default memo(ModelStoreDialogContent);
